import { SparklesIcon } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  fetchFactoryMonitoringLines,
  isDemoMode,
  runFactoryMonitoringAgent,
  type FactoryMonitoringFamily,
  type FactoryMonitoringLinePreview,
  useAppDispatch
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { Checkbox } from '../../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { useChatAppContext } from '../../chat-app-context'
import { isCoreView, useOperatorView } from '../../lib/operator-view-flag'
import {
  buildAgentConfigAppContextEntries,
  CONFIGURE_SUPERSEDED_PARAMS
} from './configure-app-context'
import { ConfigUnifiedList } from './components/ConfigUnifiedList'
import CustomAgentConfigDetail from './detail/CustomAgentConfigDetail'
import NotificationRuleDetail from './detail/NotificationRuleDetail'
import { useConfigureLastVisited } from './useConfigureLastVisited'

export type ConfigureType = 'custom' | 'notification-rules'

const lineLabel = (line: FactoryMonitoringLinePreview): string => line.name ?? line.id

const FAMILY_OPTIONS: Array<{ value: FactoryMonitoringFamily; label: string }> = [
  { value: 'rca', label: 'RCA' },
  { value: 'pm', label: 'PM' },
  { value: 'bottleneck', label: 'BNA' },
  { value: 'yield_quality', label: 'YQ' }
]
const ALL_FAMILIES: FactoryMonitoringFamily[] = FAMILY_OPTIONS.map((f) => f.value)

function parseType(raw: string | null): ConfigureType {
  return raw === 'notification-rules' ? raw : 'custom'
}

const ConfigurePage = () => {
  const demo = isDemoMode()
  const coreView = isCoreView(useOperatorView())
  const [searchParams] = useSearchParams()
  const rawType = searchParams.get('type')
  const type = parseType(rawType)
  // A decommissioned deep link (?type=rca&id=…) coerces to the Custom Agents
  // LIST — the id belongs to a config surface that no longer renders, so drop
  // it rather than show a Custom detail for an unrelated id.
  const coercedFromRetiredType =
    rawType !== null && rawType !== 'custom' && rawType !== 'notification-rules'
  const id = coercedFromRetiredType ? null : searchParams.get('id')
  const isBareHome = rawType === null && searchParams.get('id') === null

  useConfigureLastVisited({ type, id, isBareHome })

  // Detail views register the enriched `Config` entry themselves once the
  // row has loaded, so the page only speaks for the list.
  useChatAppContext(
    id === null ? buildAgentConfigAppContextEntries({ agentType: type }) : undefined,
    CONFIGURE_SUPERSEDED_PARAMS
  )

  const dispatch = useAppDispatch()
  const [isConfiguring, setIsConfiguring] = useState(false)
  const [isLinePickerOpen, setIsLinePickerOpen] = useState(false)
  const [isLoadingLines, setIsLoadingLines] = useState(false)
  const [lineLoadError, setLineLoadError] = useState<string | null>(null)
  const [lineQuery, setLineQuery] = useState('')
  const [lines, setLines] = useState<FactoryMonitoringLinePreview[]>([])
  const [linesTruncated, setLinesTruncated] = useState(false)
  // Per-line family selection: a line maps to the set of families to configure
  // for it. An empty/absent set means the line is skipped entirely.
  const [lineFamilies, setLineFamilies] = useState<Map<string, Set<FactoryMonitoringFamily>>>(
    () => new Map()
  )

  const filteredLines = useMemo(() => {
    const query = lineQuery.trim().toLowerCase()
    if (query.length === 0) return lines
    return lines.filter((line) => {
      const haystack = `${line.name ?? ''} ${line.id} ${line.site_id ?? ''}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [lineQuery, lines])

  const selectedLineCount = useMemo(() => {
    let count = 0
    for (const families of lineFamilies.values()) {
      if (families.size > 0) count += 1
    }
    return count
  }, [lineFamilies])

  const allFilteredSelected =
    filteredLines.length > 0 &&
    filteredLines.every((line) => (lineFamilies.get(line.id)?.size ?? 0) === ALL_FAMILIES.length)

  const openLinePicker = useCallback(async () => {
    if (isConfiguring || isLoadingLines) return
    setIsLinePickerOpen(true)
    setIsLoadingLines(true)
    setLineLoadError(null)
    setLineQuery('')
    try {
      const preview = await fetchFactoryMonitoringLines()
      setLines(preview.lines)
      setLinesTruncated(preview.lines_truncated)
      // Default every line to all families (matching the prior select-all).
      setLineFamilies(new Map(preview.lines.map((line) => [line.id, new Set(ALL_FAMILIES)])))
    } catch (err) {
      setLineLoadError(err instanceof Error ? err.message : String(err))
      setLines([])
      setLinesTruncated(false)
      setLineFamilies(new Map())
    } finally {
      setIsLoadingLines(false)
    }
  }, [isConfiguring, isLoadingLines])

  const toggleLineFamily = useCallback(
    (lineId: string, family: FactoryMonitoringFamily, checked: boolean) => {
      setLineFamilies((current) => {
        const next = new Map(current)
        const families = new Set(next.get(lineId))
        if (checked) families.add(family)
        else families.delete(family)
        next.set(lineId, families)
        return next
      })
    },
    []
  )

  const toggleFilteredLines = useCallback(() => {
    setLineFamilies((current) => {
      const next = new Map(current)
      const shouldSelect = !filteredLines.every(
        (line) => (next.get(line.id)?.size ?? 0) === ALL_FAMILIES.length
      )
      for (const line of filteredLines) {
        next.set(line.id, shouldSelect ? new Set(ALL_FAMILIES) : new Set())
      }
      return next
    })
  }, [filteredLines])

  const onAutoConfigure = useCallback(async () => {
    if (isConfiguring || selectedLineCount === 0) return
    const selection: Record<string, FactoryMonitoringFamily[]> = {}
    for (const [lineId, families] of lineFamilies) {
      if (families.size > 0) selection[lineId] = Array.from(families)
    }
    setIsLinePickerOpen(false)
    setIsConfiguring(true)
    try {
      await dispatch(runFactoryMonitoringAgent({ lineFamilies: selection }))
    } finally {
      setIsConfiguring(false)
    }
  }, [dispatch, isConfiguring, lineFamilies, selectedLineCount])

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-card">
      {!id && (
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">Configure</h1>
            <p className="mt-1 text-13 text-ink-3">
              Configure agents that watch over the ontology.
            </p>
          </div>
          {/* The factory-monitoring CTA fans out template-seeded custom agents
            per line; it stays a Pro-only affordance. */}
          {!coreView && (
            <Button
              onClick={openLinePicker}
              disabled={demo || isConfiguring || isLoadingLines}
              title={demo ? 'Not available in this demo' : undefined}
            >
              <SparklesIcon className="size-4" />
              {demo
                ? 'Factory monitoring unavailable in demo'
                : isConfiguring
                  ? 'Configuring…'
                  : 'Configure monitoring for my factory'}
            </Button>
          )}
        </header>
      )}

      <Dialog open={isLinePickerOpen} onOpenChange={setIsLinePickerOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select factory lines</DialogTitle>
            <DialogDescription>
              Choose which of RCA, PM, Bottleneck (BNA), and Yield & Quality (YQ) monitoring to
              configure for each line. RCA builds from that line&apos;s enabled alarms.
            </DialogDescription>
          </DialogHeader>

          {isLoadingLines ? (
            <div className="rounded-lg border border-border bg-card p-4 text-sm text-ink-3">
              Loading factory lines…
            </div>
          ) : lineLoadError !== null ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {lineLoadError}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Input
                  value={lineQuery}
                  onChange={(event) => setLineQuery(event.target.value)}
                  placeholder="Search lines"
                  className="sm:max-w-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleFilteredLines}
                  disabled={filteredLines.length === 0}
                >
                  {allFilteredSelected ? 'Clear shown' : 'Select shown'}
                </Button>
              </div>
              {linesTruncated && (
                <p className="m-0 text-xs text-amber-700">
                  Inventory was truncated by the server; refine the ontology inventory if expected
                  lines are missing.
                </p>
              )}
              <div className="max-h-105 overflow-auto rounded-lg border border-border">
                {filteredLines.length === 0 ? (
                  <div className="p-4 text-sm text-ink-3">No matching lines found.</div>
                ) : (
                  filteredLines.map((line) => {
                    const families = lineFamilies.get(line.id)
                    return (
                      <div
                        key={line.id}
                        className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0 hover:bg-muted/40"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink">
                            {lineLabel(line)}
                          </span>
                          <span className="block truncate text-xs text-ink-3">{line.id}</span>
                          <span className="mt-2 flex flex-wrap gap-3">
                            {FAMILY_OPTIONS.map((option) => (
                              <label
                                key={option.value}
                                className="flex cursor-pointer items-center gap-1.5 text-xs text-ink-2"
                              >
                                <Checkbox
                                  checked={families?.has(option.value) ?? false}
                                  onCheckedChange={(checked) =>
                                    toggleLineFamily(line.id, option.value, checked === true)
                                  }
                                />
                                {option.label}
                              </label>
                            ))}
                          </span>
                        </span>
                        <span className="shrink-0 text-right text-xs text-ink-3">
                          {line.equipment_count} equipment
                          <br />
                          {line.tag_count} tags
                        </span>
                      </div>
                    )
                  })
                )}
              </div>
              <p className="m-0 text-xs text-ink-3">
                {selectedLineCount} of {lines.length} line(s) selected.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsLinePickerOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onAutoConfigure}
              disabled={
                isLoadingLines || lineLoadError !== null || selectedLineCount === 0 || isConfiguring
              }
            >
              <SparklesIcon className="size-4" />
              {isConfiguring ? 'Configuring…' : 'Configure selected lines'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-key on the (type, id?) tuple so the inner view fades in on
        list → detail and detail → list transitions. AppLayout's outlet
        already does this for pathname changes; query-param changes
        (which is how this page navigates) don't change pathname, so
        the same animation has to be triggered here. */}
      <div key={id ? `detail:${type}:${id}` : 'list'} className="duration-200 animate-in fade-in">
        {id ? (
          <>
            {type === 'custom' && <CustomAgentConfigDetail id={id} />}
            {type === 'notification-rules' && <NotificationRuleDetail id={id} />}
          </>
        ) : (
          <ConfigUnifiedList />
        )}
      </div>
    </div>
  )
}

export default ConfigurePage
